// components/clients/ClientSurvey.js
// Customer Satisfaction Survey Component

import React, { useState, useEffect } from 'react';
import {
  X, Send, Star, ThumbsUp, ThumbsDown, CheckCircle, AlertCircle,
  MessageSquare, TrendingUp, Award, Calendar, User, Mail
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

const ClientSurvey = ({ 
  client, 
  onClose, 
  currentTenant, 
  user 
}) => {
  const [surveyData, setSurveyData] = useState({
    overallSatisfaction: 0,
    serviceQuality: 0,
    communication: 0,
    valueForMoney: 0,
    likelihoodToRecommend: 0,
    strengths: '',
    improvements: '',
    additionalComments: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [existingSurveys, setExistingSurveys] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    loadSurveyHistory();
  }, [client.id]);

  const loadSurveyHistory = async () => {
    try {
      const { data, error } = await supabase
        .from('client_surveys')
        .select(`
          *,
          sent_by:sent_by_user_id (full_name, email)
        `)
        .eq('client_id', client.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setExistingSurveys(data || []);
    } catch (err) {
      console.error('Error loading survey history:', err);
    }
  };

  const handleRatingChange = (field, value) => {
    setSurveyData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    // Validate that at least overall satisfaction is rated
    if (surveyData.overallSatisfaction === 0) {
      setError('Please provide an overall satisfaction rating');
      setIsSubmitting(false);
      return;
    }

    try {
      const { data, error: insertError } = await supabase
        .from('client_surveys')
        .insert([{
          tenant_id: currentTenant.id,
          client_id: client.id,
          sent_by_user_id: user.id,
          overall_satisfaction: surveyData.overallSatisfaction,
          service_quality: surveyData.serviceQuality,
          communication: surveyData.communication,
          value_for_money: surveyData.valueForMoney,
          likelihood_to_recommend: surveyData.likelihoodToRecommend,
          strengths: surveyData.strengths.trim() || null,
          improvements: surveyData.improvements.trim() || null,
          additional_comments: surveyData.additionalComments.trim() || null,
          survey_status: 'draft', // Can be 'draft', 'sent', 'completed'
          sent_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (insertError) throw insertError;

      setSuccess(true);
      loadSurveyHistory();

      // Reset form after 2 seconds and close
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (err) {
      console.error('Error saving survey:', err);
      setError(err.message || 'Failed to save survey. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const RatingStars = ({ value, onChange, label }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      <div className="flex items-center space-x-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="transition-all hover:scale-110"
          >
            <Star
              className={`w-8 h-8 ${
                star <= value
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-slate-300 hover:text-yellow-400'
              }`}
            />
          </button>
        ))}
        <span className="ml-2 text-sm text-slate-600">
          {value > 0 ? `${value}/5` : 'Not rated'}
        </span>
      </div>
    </div>
  );

  const NPSScore = ({ value, onChange, label }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      <div className="flex items-center space-x-1">
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
          <button
            key={score}
            type="button"
            onClick={() => onChange(score)}
            className={`w-10 h-10 rounded-lg border-2 font-medium text-sm transition-all ${
              score === value
                ? score >= 9
                  ? 'bg-green-500 border-green-600 text-white'
                  : score >= 7
                  ? 'bg-yellow-500 border-yellow-600 text-white'
                  : 'bg-red-500 border-red-600 text-white'
                : 'border-slate-300 text-slate-600 hover:border-slate-400'
            }`}
          >
            {score}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-xs text-slate-500 mt-1">
        <span>Not likely</span>
        <span>Very likely</span>
      </div>
    </div>
  );

  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">Survey Saved!</h3>
          <p className="text-slate-600">
            The survey has been saved successfully. You can send it to the client via email.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="text-2xl font-bold text-slate-900">Customer Satisfaction Survey</h3>
            <p className="text-sm text-slate-600 mt-1">for {client.name}</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm"
            >
              {showHistory ? 'Hide History' : 'View History'}
            </button>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {showHistory && existingSurveys.length > 0 && (
          <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
            <h4 className="font-semibold text-slate-900 mb-3">Survey History</h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {existingSurveys.map((survey) => (
                <div key={survey.id} className="bg-white p-3 rounded-lg border border-slate-200 text-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Star className="w-4 h-4 text-yellow-500" />
                      <span className="font-medium">{survey.overall_satisfaction}/5</span>
                      <span className="text-slate-500">
                        {new Date(survey.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      survey.survey_status === 'completed' ? 'bg-green-100 text-green-700' :
                      survey.survey_status === 'sent' ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {survey.survey_status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-800">{error}</p>
              </div>
              <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Rating Sections */}
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
              <h4 className="font-semibold text-slate-900 mb-4 flex items-center space-x-2">
                <Award className="w-5 h-5 text-blue-600" />
                <span>Overall Satisfaction</span>
              </h4>
              <RatingStars
                value={surveyData.overallSatisfaction}
                onChange={(val) => handleRatingChange('overallSatisfaction', val)}
                label="How satisfied are you with our overall service?"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-slate-200 rounded-xl p-6">
                <RatingStars
                  value={surveyData.serviceQuality}
                  onChange={(val) => handleRatingChange('serviceQuality', val)}
                  label="Service Quality"
                />
              </div>

              <div className="border border-slate-200 rounded-xl p-6">
                <RatingStars
                  value={surveyData.communication}
                  onChange={(val) => handleRatingChange('communication', val)}
                  label="Communication"
                />
              </div>

              <div className="border border-slate-200 rounded-xl p-6">
                <RatingStars
                  value={surveyData.valueForMoney}
                  onChange={(val) => handleRatingChange('valueForMoney', val)}
                  label="Value for Money"
                />
              </div>

              <div className="border border-slate-200 rounded-xl p-6">
                <NPSScore
                  value={surveyData.likelihood_to_recommend}
                  onChange={(val) => handleRatingChange('likelihood_to_recommend', val)}
                  label="Likelihood to Recommend"
                />
              </div>
            </div>
          </div>

          {/* Open-ended Questions */}
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                What do you value most about our partnership?
              </label>
              <textarea
                value={surveyData.strengths}
                onChange={(e) => setSurveyData(prev => ({ ...prev, strengths: e.target.value }))}
                rows={4}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder="Tell us what we're doing well..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                How can we improve our services?
              </label>
              <textarea
                value={surveyData.improvements}
                onChange={(e) => setSurveyData(prev => ({ ...prev, improvements: e.target.value }))}
                rows={4}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder="Your suggestions for improvement..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Additional Comments
              </label>
              <textarea
                value={surveyData.additionalComments}
                onChange={(e) => setSurveyData(prev => ({ ...prev, additionalComments: e.target.value }))}
                rows={4}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder="Any other feedback you'd like to share..."
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Save Survey</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientSurvey;